import { useDispatch } from 'react-redux';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-material.css';
import 'assets/third-party/ag-theme-material-dark.scss';
import useAuth from '../../hooks/useAuth';
import { useCallback, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Rights,
  useAssignAccessToPortfolioMutation,
  User,
  useRemoveAccessToPortfolioMutation,
  useUserLazyQuery
} from '../../graphql/types';
import InvestorDropDownAutoComplete from '../../components/InvestorDropDownAutoComplete';
import { Box, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import MainCard from '../../components/MainCard';
import ScrollX from '../../components/ScrollX';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ICellRendererParams, IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-enterprise';
import { DefaultAgGridTooltip } from '../../components/ag-grid/DefaultAgGridTooltip';
import UserAvatar from './UserAvatar';
import { getUserProfileFullName } from '../../utils/users';
import { StopOutlined } from '@ant-design/icons';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { GridReadyEvent } from 'ag-grid-community';
import useConfig from '../../hooks/useConfig';
import { openSnackbar } from '../../store/reducers/snackbar';

enum AccessTypeEnum {
  To = 'to',
  From = 'from'
}

interface Props {
  entity: User;
  accessType: AccessTypeEnum;
}

const AccessTab = ({ entity, accessType }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { mode } = useConfig();
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const { user: currentUser } = useAuth();
  const canManage = !!currentUser?.rights.includes(Rights.ManageUsers);
  const { height, width } = useWindowDimensions();

  const createSnackbar = (dispatch) => (message, color) => {
    dispatch(
      openSnackbar({
        open: true,
        message: message,
        variant: 'alert',
        alert: {
          color: color
        },
        close: true
      })
    );
  };
  const snackBar = createSnackbar(dispatch);

  const handleInvestorChange = (selectedUser: Partial<User> | null) => {
    let flagDuplicate = false;
    gridApi.forEachNode((node) => {
      const idToCompare = accessType === AccessTypeEnum.To ? node.data.targetUser.id : node.data.sourceUser.id;
      if (idToCompare === selectedUser?.id) {
        flagDuplicate = true;
      }
    });

    if (flagDuplicate) createSnackbar('Connection already exists', 'error');
    else if (selectedUser?.id === entity.id) snackBar('Invalid connection request (Self Reference)', 'error');
    else if (selectedUser?.id !== undefined) {
      if (accessType === AccessTypeEnum.To) handleAssignAccess(entity.id, selectedUser?.id);
      else handleAssignAccess(selectedUser?.id, entity.id);
    }
  };

  const [assignAccessToPortfolio] = useAssignAccessToPortfolioMutation({
    onCompleted: () => {
      refreshData();
      snackBar('Connection added successfully', 'success');
    },
    onError: (error) => {
      snackBar('An error occurred while assigning connection', 'error');
    }
  });

  const handleAssignAccess = (sourceUserId: string, targetUserId: string) => {
    assignAccessToPortfolio({
      variables: {
        sourceUserId: sourceUserId,
        targetUserId: targetUserId
      }
    });
  };

  const [removeAccessToPortfolio] = useRemoveAccessToPortfolioMutation({
    onCompleted: () => {
      refreshData();
      snackBar('Connection removed successfully', 'success');
    },
    onError: (error) => {
      snackBar('An error occurred while assigning connection', 'error');
    }
  });

  const handleRemoveAccess = (sourceUserId: string, targetUserId: string) => {
    removeAccessToPortfolio({
      variables: {
        sourceUserId: sourceUserId,
        targetUserId: targetUserId
      }
    });
  };

  const onGridReady = useCallback(({ api }: GridReadyEvent) => {
    setGridApi(api);
    api.updateGridOptions({
      serverSideDatasource
    });
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      minWidth: 120,
      editable: false,
      sortable: true,
      resizable: true,
      pivot: true,
      filter: false,
      flex: 1,
      tooltipComponent: DefaultAgGridTooltip
    };
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Profile',
        field: 'targetUser.name',
        minWidth: 240,
        cellRenderer: ({ data }: ICellRendererParams) => {
          return (
            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <UserAvatar user={accessType === AccessTypeEnum.To ? data.targetUser : data.sourceUser} showBadge={false} />
                <Typography variant="subtitle1" style={{ lineHeight: '1.2', marginLeft: '8px' }}>
                  {getUserProfileFullName(accessType === AccessTypeEnum.To ? data.targetUser : data.sourceUser)}
                </Typography>
              </Stack>
            </div>
          );
        }
      },
      {
        headerName: '',
        hide: !canManage,
        maxWidth: 146,
        minWidth: 146,
        sortable: false,
        enablePivot: false,
        pivot: false,
        resizable: true,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        aggFunc: null,
        pinned: 'right',
        suppressColumnsToolPanel: true,
        cellClass: 'no-border',
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          const { id, name } = accessType === AccessTypeEnum.To ? data.targetUser : data.sourceUser;
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
                <Tooltip title="Delete">
                  <IconButton
                    color="error"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      accessType === AccessTypeEnum.To ? handleRemoveAccess(entity.id, id) : handleRemoveAccess(id, entity.id);
                    }}
                  >
                    <StopOutlined twoToneColor={theme.palette.error.main} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </div>
          );
        }
      }
    ],
    []
  );

  const [fetchUserData] = useUserLazyQuery({
    fetchPolicy: 'network-only'
  });

  const serverSideDatasource: IServerSideDatasource = {
    getRows: async (params: IServerSideGetRowsParams) => {
      if (params.api.isDestroyed()) { return; }
      if (!entity?.id) {
        params.fail();
        return;
      }

      const res = await fetchUserData({
        variables: {
          id: entity?.id
        }
      });
      if (params.api.isDestroyed()) { return; }

      const user = res?.data?.user || null;

      if (!user) {
        params.fail();
        return;
      }

      accessType === AccessTypeEnum.To
        ? params.success({ rowData: user?.portfolioConnections })
        : params.success({ rowData: user?.portfolioConnectedTo });
      params.api.sizeColumnsToFit();
    }
  };

  const refreshData = () => {
    if (gridApi) {
      gridApi.refreshServerSide();
      gridApi.refreshCells({ force: true });
    }
  };

  return (
    <MainCard content={false}>
      <ScrollX>
        <Grid container alignItems="center" spacing={2} sx={{ padding: 2, paddingLeft: 3 }}>
          {' '}
          <Grid item>
            <Typography variant="h6">Select an investor to add:</Typography>
          </Grid>
          <Grid item>
            <InvestorDropDownAutoComplete onInvestorChange={handleInvestorChange} />
          </Grid>
        </Grid>
        <Box display="flex" flexDirection="column">
          <div className={mode === 'light' ? 'ag-theme-material' : 'ag-theme-material-dark'} style={gridStyle}>
            <AgGridReact<User[]>
              columnDefs={columnDefs}
              domLayout={'autoHeight'}
              rowHeight={40}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              rowModelType={'serverSide'}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
            ></AgGridReact>
          </div>
        </Box>
      </ScrollX>
    </MainCard>
  );
};

export default AccessTab;
